import React, { useState, useEffect } from 'react'
import i18n from 'live/services/i18n'
import { Button } from 'react-bootstrap'
import { ConfigBar, EditorTheme } from 'plugin-assets/shared/widget/components/styled'
import styled from 'styled-components'
import WellImportData from '../../../wellimport/WellImport/components/WellImportData'
import { ConstantsImport } from '../../../wellimport/WellImport/constants/ConstantsImport'
import ModalImportCSB from './ModalImportCSB'
import ModalEditQuery from './ModalEditQuery'
import FileIcon from './FileIcon'
import widgetService from 'live/services/widgets'
import LiveTooltip from 'live/components/ui/tooltip'
import _ from 'lodash'

type Props = {
    onSelectFile: Function
    file?: { name: string; wellName: string }
    defaultQuery: string
    manualQuery: string
    setEventType: Function
    eventType: string
    onApplyManualQuery: (query: string) => void
}
export default function Sidebar(props: Props) {
    const { onSelectFile, file, defaultQuery, manualQuery, onApplyManualQuery, eventType, setEventType } = props
    const [showModalImportCSB, setShowModalImportCSB] = useState(false)
    const [showModalEditQuery, setShowModalEditQuery] = useState(false)
    const [submited, setSubmited] = useState(false)
    const [enable, setEnable] = useState(false)

    const removeFile = () => {
        onSelectFile({ svg: null, file: null })
        setSubmited(false)
        setEnable(false)
    }

    const handleOnSelectFile = (response: { svg: string; file: { name: string; wellName: string } }) => {
        if (submited) {
            onSelectFile(response)
            setShowModalImportCSB(false)
            setEnable(false)
            setSubmited(false)
        }
    }

    const isDefaultQuery = !manualQuery || manualQuery === defaultQuery

    return (
        <ConfigBar dark={false}>
            <EditorTheme>
                <Header>
                    <Title>{i18n('Chart settings')}</Title>
                </Header>
                <Main>
                    <HeadMain>
                        <LabelContainer>
                            <label>{i18n('Well normalized event type')}</label>
                            <LiveTooltip
                                position="top"
                                content={i18n(
                                    'Event types or macros from lookup tables. If the field is empty, the default lookup table will be used'
                                )}
                            >
                                <div>
                                    <i className="far fa-question-circle" />
                                </div>
                            </LiveTooltip>
                        </LabelContainer>

                        <LiveTooltip
                            hide={!isDefaultQuery}
                            position="top"
                            content={i18n('This field is blocked because you are using manual query')}
                        >
                            <div>
                                <input
                                    disabled={!isDefaultQuery}
                                    type="text"
                                    placeholder={i18n('Type here')}
                                    onChange={e => setEventType(e.target.value)}
                                    value={eventType}
                                />
                            </div>
                        </LiveTooltip>
                        <LiveTooltip
                            hide={!file}
                            position="top"
                            content={i18n('Remove the imported schematic before importing a new one.')}
                        >
                            <div>
                                <Btn
                                    disabled={file}
                                    onClick={() => setShowModalImportCSB(true)}
                                    variant="outline-primary"
                                >
                                    <i className="fas fa-file-import" />
                                    <label>{i18n('Import schematic from CSB')}</label>
                                </Btn>
                            </div>
                        </LiveTooltip>
                        {file && (
                            <File>
                                <div>
                                    <FileIcon name={'CSB'} color={'#3a8352'} />
                                    <div>{file?.wellName + ' / ' + file?.name}</div>
                                </div>
                                <i className="far fa-times-circle" onClick={removeFile} />
                            </File>
                        )}
                    </HeadMain>
                    {widgetService.canManageDefaultWidgets() && (
                        <FooterMain>
                            <TitleEditQuery>
                                <i className="fas fa-code" />
                                <div
                                    onClick={() => {
                                        setShowModalEditQuery(true)
                                    }}
                                >
                                    {i18n('Edit query (for advanced users)')}
                                </div>
                            </TitleEditQuery>
                        </FooterMain>
                    )}
                </Main>
                {/* Modal for import csb */}
                <ModalImportCSB
                    setSubmit={setSubmited}
                    disable={!enable}
                    showModal={showModalImportCSB}
                    setShowModal={setShowModalImportCSB}
                >
                    <ContainerFiles>
                        <WellImportData
                            onSelectFile={handleOnSelectFile}
                            typesListShow={ConstantsImport.attachmentTypes}
                            setEnable={setEnable}
                            submited={submited}
                        />
                    </ContainerFiles>
                </ModalImportCSB>
                {/* Modal for edit query */}
                <ModalEditQuery
                    showModal={showModalEditQuery}
                    applyQuery={onApplyManualQuery}
                    setShowModal={setShowModalEditQuery}
                    defaultQuery={defaultQuery}
                    manualQuery={manualQuery}
                />
            </EditorTheme>
        </ConfigBar>
    )
}

const ContainerFiles = styled.div`
    overflow: auto;
    .borderItem {
        height: 50px;
    }
`

type TabOptionProps = {
    active: boolean
    disabled: boolean
}

const TabOption = styled.div`
    cursor: ${({ active, disabled }: TabOptionProps) => (!active && !disabled ? 'pointer' : 'default')};
    user-select: none;
    width: 78px;
    border-bottom: ${({ active, disabled }: TabOptionProps) =>
        active ? 'solid 2px #37a3ef' : 'solid 2px transparent'};
    color: ${({ active, disabled }: TabOptionProps) => (disabled ? 'lightgrey' : active ? 'inherit' : '#37a3ef')};
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    font-family: ${({ theme }) => theme['font-semibold']};
`

const Header = styled.header`
    border-bottom: 1px solid #dddee1;
    padding: 18px 16px 13px 16px;
`

const Title = styled.div`
    font-family: ${({ theme }) => theme['font-semibold']};
    font-weight: 600;
    font-size: 14px;
    text-transform: uppercase;
    color: #606a80;
`

const Main = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    > label {
        font-family: ${({ theme }) => theme['font-semibold']};
        font-style: normal;
        font-weight: 600;
        font-size: 13px;
        color: #364159;
    }

    > input {
        height: 32px;
        margin-top: 3px;
    }
`

const Btn = styled(Button)`
    width: 100%;
    height: 30px;
    display: flex;
    align-items: center;
    margin-top: 18px;
    border-radius: 2px;
    justify-content: center;
    border-color: #37a3ef;
    background: #dfeaef;
    color: #37a3ef;
    > label {
        margin-left: 9px;
        color: #37a3ef;
        cursor: pointer;
    }
    &:hover {
        border-color: #37a3ef;
        background: #f3fbff;
        color: #37a3ef;
    }
    &:focus {
        border-color: #37a3ef;
        background: #f3fbff;
        color: #37a3ef;
    }
    &:disabled {
        border-color: #37a3ef;
        background: #dfeaef;
        color: #37a3ef;
    }
`

const File = styled.div`
    margin: 0;
    margin-top: 11px;
    height: 36px;
    background: #dddee1;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-overflow: ellipsis;
    white-space: nowrap;
    > div {
        display: flex;
        justify-content: center;
        align-items: center;
        > div {
            font-style: normal;
            font-weight: 600;
            font-size: 14px;
            font-family: ${({ theme }) => theme['font-semibold']};
            color: #364159;
            overflow: hidden;
            max-width: 30ch;
        }
    }
    > i {
        font-size: 14px;
        color: #606a80;
        font-style: normal;
        font-weight: normal;
        padding-right: 13px;
        cursor: pointer;
    }
`

const TitleEditQuery = styled.div`
    display: flex;
    padding: 18px 16px 13px 16px;
    border-top: 1px solid #dddee1;
    align-items: center;
    flex-basis: 0;
    justify-content: flex-start;
    > i {
        margin-right: 10px;
        font-style: normal;
        font-weight: normal;
        font-size: 16px;
        color: #868695;
    }

    > div {
        cursor: pointer;
        font-style: normal;
        font-weight: 600;
        font-size: 13px;
        font-family: ${({ theme }) => theme['font-semibold']};
        color: #37a3ef;
    }
`

const HeadMain = styled.div`
    padding: 15px 16px 0 16px;
`

const FooterMain = styled.div``

const LabelContainer = styled.div`
    display: flex;
    flex-direction: row;
    i {
        padding-left: 5px;
        color: #37a3ef;
    }
`
